import React, { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Divider, Typography } from '@mui/material';
import { PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import AddButton from 'app/shared/components/common/AddButton';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { deleteByIndex } from 'utils/data';

import { selectBorrowerOptionsCommonInfo } from 'store/loanApplications/selectors';
import { selectDenialReasonsData, selectLoanActionFieldsMap } from 'store/system/selectors';

import { LoanHeadersTestIds } from '../../LoanHeaders.testIds';

import DenialReasonField from './DenialReasonField';
import { DenialReasonFieldsSectionTestIds } from './DenialReasonFieldsSection.testIds';

const DenialReasonFieldsSection = ({
  field,
  formDenialReasons,
  isLoading,
  handleChange,
  setInForm,
  setValidationErrors,
  validationErrors,
}) => {
  const loanActionFieldsMap = useSelector(selectLoanActionFieldsMap);
  const allDenialReasonsData = useSelector(selectDenialReasonsData);
  const borrowerOptions = useSelector(selectBorrowerOptionsCommonInfo);
  const [isEmptyFieldActive, setIsEmptyFieldActive] = useState(true);

  const { required: isRequired, fieldId } = field;

  const options = useMemo(() => {
    return loanActionFieldsMap[fieldId].options.filter(
      option => !formDenialReasons?.some(({ value }) => value === option.value),
    );
  }, [formDenialReasons, loanActionFieldsMap, fieldId]);

  const onShowNewField = useCallback(() => setIsEmptyFieldActive(true), []);

  const onDeleteDenialReason = useCallback(
    e => {
      const { id: index } = e.currentTarget.dataset;
      const updatedDenialReasons = deleteByIndex(formDenialReasons, index);

      setInForm('denialReasons', updatedDenialReasons);

      if (updatedDenialReasons.length === 0) {
        setIsEmptyFieldActive(true);
      }
    },
    [formDenialReasons, setInForm],
  );

  const onAddDenialReason = useCallback(
    (e, { value, name }) => {
      const index = formDenialReasons?.length ?? 0;
      const denialReasonData = allDenialReasonsData[value];

      setInForm(`denialReasons[${index}]`, {
        value,
        name,
        ...(denialReasonData.isDenialDueToCredit && {
          borrowers: borrowerOptions.map(({ value }) => ({
            id: value,
            value: borrowerOptions.length === 1,
          })),
        }),
      });
      setValidationErrors(errors => _.omit(errors, 'denialReasons'));
      setIsEmptyFieldActive(false);
    },
    [allDenialReasonsData, borrowerOptions, formDenialReasons, setInForm, setValidationErrors],
  );

  const denialReasonsErrors = validationErrors.denialReasons;
  const commonErrorText = _.isString(denialReasonsErrors) ? denialReasonsErrors : null;
  const fieldErrors = _.isArray(denialReasonsErrors) ? denialReasonsErrors : null;

  return (
    <div className="mb-24 min-w-sm">
      <Typography className="-mb-12" variant="medium14">
        Denial Reasons {isRequired && '*'}
      </Typography>
      <div className="flex items-center justify-between">
        <Divider className="flex-1" />
        <AddButton
          disablePortal
          ariaLabel="Add Denial Reason"
          dataTestId={LoanHeadersTestIds.ADD_DENIAL_REASONS_ITEM_BUTTON}
          disabled={isLoading || options.length === 0}
          icon={PlusIcon}
          styleConfig="relative"
          onClick={onShowNewField}
        />
      </div>

      {formDenialReasons?.map((denialReason, i) => (
        <DenialReasonField
          allDenialReasonsData={allDenialReasonsData}
          borrowerOptions={borrowerOptions}
          denialReason={denialReason}
          handleChange={handleChange}
          index={i}
          isLoading={isLoading}
          key={denialReason.value}
          validationErrors={fieldErrors}
          onDeleteDenialReason={onDeleteDenialReason}
        />
      ))}
      {isEmptyFieldActive && (
        <AutocompleteCDS
          dataTestId={DenialReasonFieldsSectionTestIds.NEW_DENIAL_REASON_SELECT}
          error={!!commonErrorText}
          helperText={commonErrorText}
          label="Select Denial Reason"
          name="newDenialReason"
          optionName="name"
          options={options}
          value={null}
          onChange={onAddDenialReason}
        />
      )}
    </div>
  );
};

DenialReasonFieldsSection.propTypes = {
  ...WithLoadingPropTypes,
  field: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  setValidationErrors: PropTypes.func.isRequired,
  validationErrors: PropTypes.object,
  formDenialReasons: PropTypes.array,
};

export default memo(DenialReasonFieldsSection);
